import type { BeatLineViteConfig } from './types';
import * as localCache from './localCache';

type AnyRecord = Record<string, any>;

type RouteStat = {
  rest: number;
  local: number;
  fallback: number;
  last?: string;
  lastError?: string;
};

type FullBridgeState = {
  version: string;
  installedAt: string;
  actions: Record<string, RouteStat>;
};

type RestCandidate = {
  method: 'GET' | 'POST';
  path: string;
  include?: string;
  resultKey?: string;
};

const VERSION = 'beatline_rest_migration_final_v6_prewarm_appdata_fetch';
const SUMMARY_KEY = 'beatline_c74_full_migration_summary';
const RAW_KEY = 'beatline_c74_full_migration_raw';

const ACTIONS: Record<string, RestCandidate[]> = {
  // beatline_get_data tijdelijk bewust NIET via REST.
  // De REST app-data is bereikbaar, maar de structuur matcht nog niet 1-op-1 met de oude playlist-loader.
  // Daardoor blijven playlists nu via de bestaande admin-ajax route lopen.
beatline_heard_stats: [
    { method: 'GET', path: '/wp-json/beatline/v1/shadow/batch', include: 'heard-stats', resultKey: 'heard-stats' },
    { method: 'GET', path: '/wp-json/beatline/v1/heard-stats', resultKey: 'heard-stats' }
  ],

  beatline_heard_get: [
    { method: 'GET', path: '/wp-json/beatline/v1/shadow/batch', include: 'heard-get', resultKey: 'heard-get' }
  ],

  beatline_get_song_meta: [
    { method: 'GET', path: '/wp-json/beatline/v1/shadow/batch', include: 'song-meta', resultKey: 'song-meta' }
  ],

  beatline_heard_commit: [
    { method: 'POST', path: '/wp-json/beatline/v1/heard-commit' },
    { method: 'POST', path: '/wp-json/beatline/v1/heard/commit' }
  ],

  beatline_validate_placement: [
    { method: 'POST', path: '/wp-json/beatline/v1/validate-placement' },
    { method: 'POST', path: '/wp-json/beatline/v1/placement/validate' }
  ],

  beatline_validate_challenge: [
    { method: 'POST', path: '/wp-json/beatline/v1/validate-challenge' },
    { method: 'POST', path: '/wp-json/beatline/v1/challenge/validate' }
  ],

  beatline_validate_and_reveal: [
    { method: 'POST', path: '/wp-json/beatline/v1/validate-and-reveal' },
    { method: 'POST', path: '/wp-json/beatline/v1/reveal/validate' }
  ],

  beatline_push_register: [
    { method: 'POST', path: '/wp-json/beatline/v1/push-register' },
    { method: 'POST', path: '/wp-json/beatline/v1/push/register' }
  ]
};

const GLOBAL_FUNCTIONS: Record<string, string> = {
  beatlineHeardStats: 'beatline_heard_stats',
  beatlineHeardGet: 'beatline_heard_get',
  beatlineHeardCommit: 'beatline_heard_commit'
};

function wAny(): any {
  return window as any;
}

function trimTrailingSlash(value: string): string {
  return String(value || '').replace(/\/+$/, '');
}

function remoteBase(config: BeatLineViteConfig): string {
  const fromConfig = trimTrailingSlash(String((config as any).remoteSiteUrl || ''));
  if (fromConfig) return fromConfig;

  try {
    return trimTrailingSlash(window.location.origin || 'https://beatlinegame.com');
  } catch (_) {
    return 'https://beatlinegame.com';
  }
}

function restNonce(config: BeatLineViteConfig): string {
  return String(
    (config as any).restNonce ||
    (config as any).beatlineSettings?.restNonce ||
    ''
  );
}

function isNativeRuntime(_config: BeatLineViteConfig): boolean {
  try {
    const w = window as any;
    const href = window.location.href || '';
    const host = window.location.hostname || '';
    const protocol = window.location.protocol || '';
    const ua = navigator.userAgent || '';
    const params = new URLSearchParams(window.location.search || '');

    if (w.Capacitor || w.BeatLineNative) return true;
    if (protocol === 'capacitor:' || protocol === 'file:') return true;

    // Native Android app draait lokaal op https://localhost.
    if ((host === 'localhost' || host === '127.0.0.1') && params.get('native_app') === '1') {
      return true;
    }

    // Android WebView-detectie, maar niet voor gewone desktop/mobile browser op beatlinegame.com.
    if ((/; wv/i.test(ua) || /BeatLineNative|Capacitor|Cordova/i.test(ua)) && params.get('native_app') === '1') {
      return true;
    }

    // Publieke website blijft altijd web, ook als legacy config per ongeluk app/native zegt.
    if (/^https:\/\/beatlinegame\.com\//i.test(href)) {
      return false;
    }

    return false;
  } catch (_) {
    return false;
  }
}

function obj(value: any): AnyRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function resultData(root: AnyRecord | null, key?: string): any {
  if (!root) return null;
  if (!key) return root?.data ?? root;

  return obj(root?.data?.results?.[key]?.data)
    || root?.data?.results?.[key]?.data
    || obj(root?.data?.results?.[key])
    || root?.data?.results?.[key]
    || obj(root?.results?.[key]?.data)
    || root?.results?.[key]?.data
    || obj(root?.results?.[key])
    || root?.results?.[key]
    || obj(root?.data)
    || root?.data
    || root;
}

function normalizeResponse(json: AnyRecord | null, candidate: RestCandidate): AnyRecord {
  if (!json) throw new Error('Lege REST-response');

  if (typeof json.success === 'boolean') {
    return json;
  }

  const data = resultData(json, candidate.resultKey || candidate.include);

  if (json.ok === true || json.shadow === true || data !== null) {
    return {
      success: true,
      data
    };
  }

  throw new Error('REST-response heeft geen bruikbare success/data vorm');
}

function makeJsonResponse(data: any, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

function legacyGetDataShape(result: AnyRecord): AnyRecord {
  // Directe admin-ajax fetch voor beatline_get_data werd door de oude game-code
  // als raw app-data gelezen. Daarom mag deze route niet als { success, data }
  // terugkomen, maar moet data.playlists direct op root-niveau beschikbaar zijn.
  if (result && result.success === true && result.data && typeof result.data === 'object') {
    return result.data as AnyRecord;
  }

  return result;
}

function getState(): FullBridgeState {
  const w = wAny();

  if (!w.__beatlineC74FullMigration) {
    w.__beatlineC74FullMigration = {
      version: VERSION,
      installedAt: new Date().toISOString(),
      actions: {}
    };
  }

  return w.__beatlineC74FullMigration;
}

function stat(action: string): RouteStat {
  const state = getState();

  if (!state.actions[action]) {
    state.actions[action] = {
      rest: 0,
      local: 0,
      fallback: 0
    };
  }

  return state.actions[action];
}

function saveSummary(): void {
  try {
    localStorage.setItem(SUMMARY_KEY, JSON.stringify({ ...getState(), localCache: localCache.safeCacheDebugSnapshot() }));
  } catch (_) {}
}

function markRest(action: string, note?: string): void {
  const s = stat(action);
  s.rest += 1;
  s.last = note || 'rest';
  s.lastError = undefined;
  saveSummary();
}

function markLocal(action: string, note?: string): void {
  const s = stat(action);
  s.local = Number(s.local || 0) + 1;
  s.last = note || 'local';
  s.lastError = undefined;
  saveSummary();
}

function markFallback(action: string, error?: string): void {
  const s = stat(action);
  s.fallback += 1;
  s.last = 'fallback';
  if (error) s.lastError = error;
  saveSummary();
}

function addPayloadValue(payload: AnyRecord, key: string, value: string): void {
  if (payload[key] === undefined) {
    payload[key] = value;
    return;
  }

  if (Array.isArray(payload[key])) {
    payload[key].push(value);
    return;
  }

  payload[key] = [payload[key], value];
}

function payloadFromFormData(form: FormData): AnyRecord {
  const payload: AnyRecord = {};

  try {
    form.forEach((value, key) => {
      if (typeof value === 'string') addPayloadValue(payload, key, value);
    });
  } catch (_) {}

  return payload;
}

function payloadFromBody(body: any): AnyRecord {
  if (!body) return {};

  try {
    if (body instanceof FormData) return payloadFromFormData(body);

    if (body instanceof URLSearchParams) {
      const payload: AnyRecord = {};
      body.forEach((value, key) => {
        addPayloadValue(payload, key, value);
      });
      return payload;
    }

    if (typeof body === 'string') {
      const params = new URLSearchParams(body);
      const payload: AnyRecord = {};
      params.forEach((value, key) => {
        addPayloadValue(payload, key, value);
      });
      return payload;
    }
  } catch (_) {}

  return {};
}

function buildUrl(config: BeatLineViteConfig, candidate: RestCandidate, payload: AnyRecord): string {
  const url = new URL(`${remoteBase(config)}${candidate.path}`);

  if (candidate.include) {
    url.searchParams.set('include', candidate.include);
  }

  Object.keys(payload || {}).forEach((key) => {
    if (key === 'nonce' || key === '_wpnonce' || key === 'action') return;

    const value = payload[key];

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          url.searchParams.append(key, String(item));
        }
      });
      return;
    }

    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

    if (isNativeRuntime(config)) {
      if (isNativeRuntime(config)) {
    url.searchParams.set('native_app', '1');
  } else {
    url.searchParams.delete('native_app');
  }
  } else {
    url.searchParams.delete('native_app');
  }

  return url.toString();
}

async function callCandidate(action: string, payload: AnyRecord, config: BeatLineViteConfig, candidate: RestCandidate): Promise<AnyRecord> {
  const headers: Record<string, string> = {
    Accept: 'application/json'
  };

  const nonce = restNonce(config);
  if (nonce) headers['X-WP-Nonce'] = nonce;

  const url = buildUrl(config, candidate, payload);

  const init: RequestInit = {
    method: candidate.method,
    credentials: 'include',
    cache: 'no-store',
    mode: 'cors',
    headers
  };

  if (candidate.method === 'POST') {
    headers['Content-Type'] = 'application/json';

    const ajaxNonceForRestPost = String(
      (config as any).beatlineSettings?.nonce ||
      wAny().ajaxNonce ||
      ''
    );

    init.body = JSON.stringify({
      ...payload,
      action,
      native_app: isNativeRuntime(config) ? '1' : '0',
      ...(ajaxNonceForRestPost ? { nonce: ajaxNonceForRestPost } : {})
    });
  }

  const response = await fetch(url, init);

  let json: AnyRecord | null = null;

  try {
    json = await response.json();
  } catch (_) {
    json = null;
  }

  if (!response.ok) {
    const code = json?.code ? ` ${json.code}` : '';
    throw new Error(`HTTP ${response.status}${code}`);
  }

  try {
    localStorage.setItem(RAW_KEY, JSON.stringify({
      action,
      url,
      candidate,
      json
    }).slice(0, 4000));
  } catch (_) {}

  return normalizeResponse(json, candidate);
}

async function callRestUnqueued(action: string, payload: AnyRecord, config: BeatLineViteConfig): Promise<AnyRecord> {
  const candidates = ACTIONS[action];

  if (!candidates || !candidates.length) {
    throw new Error(`Geen REST-kandidaten voor ${action}`);
  }

  const errors: string[] = [];

  for (const candidate of candidates) {
    try {
      const result = await callCandidate(action, payload || {}, config, candidate);
      markRest(action, `${candidate.method} ${candidate.path}${candidate.include ? ` include=${candidate.include}` : ''}`);
      return result;
    } catch (error: any) {
      errors.push(`${candidate.method} ${candidate.path}: ${String(error?.message || error)}`);
    }
  }

  throw new Error(errors.join(' | '));
}

let songMetaQueue: Promise<AnyRecord> = Promise.resolve({} as AnyRecord);

async function callRest(action: string, payload: AnyRecord, config: BeatLineViteConfig): Promise<AnyRecord> {
  if (action === 'beatline_push_register') {
    const cached = localCache.getFreshPushRegisterResponse(payload || {});
    if (cached) {
      markLocal(action, 'push-token-dedupe');
      return cached;
    }

    const result = await callRestUnqueued(action, payload || {}, config);
    localCache.storePushRegisterResponse(payload || {}, result);
    return result;
  }

  if (action === 'beatline_heard_get') {
    const cached = localCache.getCachedHeardGet(payload || {});
    if (cached) {
      markLocal(action, 'heard-get-read-through-cache');
      localCache.schedulePrewarmFromHeardGet(payload || {}, cached, config);
      return cached;
    }

    const result = await callRestUnqueued(action, payload || {}, config);
    localCache.storeHeardGet(payload || {}, result);
    localCache.schedulePrewarmFromHeardGet(payload || {}, result, config);
    return result;
  }

  if (action === 'beatline_heard_commit') {
    // Belangrijk: heard_commit blijft server-side. We gebruiken lokaal alleen invalidatie,
    // zodat een latere heard_get-cache nooit verouderde data teruggeeft.
    const result = await callRestUnqueued(action, payload || {}, config);
    localCache.invalidateHeardGet(payload || {});
    return result;
  }

  if (action !== 'beatline_get_song_meta') {
    const result = await callRestUnqueued(action, payload || {}, config);

    if (action === 'beatline_get_data') {
      localCache.storeAppDataFromResponse(result);
    }

    return result;
  }

  const cached = localCache.getCachedSongMetaResponse(payload || {});
  if (cached.response) {
    markLocal(action, 'song-meta-cache');
    return cached.response;
  }

  const missingPayload = localCache.payloadForMissingSongMeta(payload || {}, cached.missingIds);

  const next = songMetaQueue
    .catch(() => ({} as AnyRecord))
    .then(() => callRestUnqueued(action, missingPayload, config))
    .then((serverResult) => {
      const merged = localCache.mergeSongMetaResponse(payload || {}, serverResult);
      return merged || serverResult;
    });

  songMetaQueue = next.catch(() => ({} as AnyRecord));

  return next;
}

function parseAdminAjaxAction(input: RequestInfo | URL, init?: RequestInit): { action: string; payload: AnyRecord } | null {
  try {
    const rawUrl = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request).url;

    if (!rawUrl || rawUrl.indexOf('admin-ajax.php') === -1) return null;

    const method = String(init?.method || 'GET').toUpperCase();
    const url = new URL(rawUrl, window.location.href);
    const payload = payloadFromBody(init?.body);

    let action = payload.action || url.searchParams.get('action') || '';

    if (!action && method === 'GET') {
      action = url.searchParams.get('action') || '';
    }

    if (!action) return null;

    url.searchParams.forEach((value, key) => {
      if (payload[key] === undefined) payload[key] = value;
    });

    return {
      action: String(action),
      payload
    };
  } catch (_) {
    return null;
  }
}

function installFetchHook(config: BeatLineViteConfig): void {
  const w = wAny();

  if (w.__beatlineC74FetchHookInstalled) return;
  w.__beatlineC74FetchHookInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const parsed = parseAdminAjaxAction(input, init);

    if (parsed && ACTIONS[parsed.action] && !w.__beatlineC74BypassFetchHook) {
      try {
        const result = await callRest(parsed.action, parsed.payload, config);

        if (parsed.action === 'beatline_get_data') {
          return makeJsonResponse(legacyGetDataShape(result));
        }

        return makeJsonResponse(result);
      } catch (error: any) {
        markFallback(parsed.action, String(error?.message || error));

        if ((config as any).debug || w.BEATLINE_VITE_DEBUG) {
          console.warn('[BeatLine full REST migration] REST failed, using original admin-ajax fetch', {
            action: parsed.action,
            error: String(error?.message || error)
          });
        }
      }
    }

    return originalFetch(input as any, init as any);
  };
}

function wrapFunction(action: string, original: any, config: BeatLineViteConfig): any {
  if (typeof original !== 'function') return original;
  if (original.__beatlineC74Wrapped) return original;

  const wrapped = async function(this: any, ...args: any[]) {
    const firstArg = obj(args[0]) || {};

    try {
      return await callRest(action, firstArg, config);
    } catch (error: any) {
      markFallback(action, String(error?.message || error));

      if ((config as any).debug || wAny().BEATLINE_VITE_DEBUG) {
        console.warn('[BeatLine full REST migration] REST failed, using original global function', {
          action,
          error: String(error?.message || error)
        });
      }

      const w = wAny();

      try {
        w.__beatlineC74BypassFetchHook = true;
        return await original.apply(this, args);
      } finally {
        w.__beatlineC74BypassFetchHook = false;
      }
    }
  };

  wrapped.__beatlineC74Wrapped = true;
  wrapped.__beatlineC74Original = original;

  return wrapped;
}

function installGlobalFunctionHook(name: string, action: string, config: BeatLineViteConfig): void {
  const w = wAny();
  const internalKey = `__beatlineC74_${name}`;

  if (w[`__beatlineC74Hooked_${name}`]) return;
  w[`__beatlineC74Hooked_${name}`] = true;

  let current = typeof w[name] === 'function'
    ? wrapFunction(action, w[name], config)
    : w[name];

  try {
    Object.defineProperty(w, name, {
      configurable: true,
      enumerable: true,
      get() {
        return current;
      },
      set(value) {
        current = typeof value === 'function' ? wrapFunction(action, value, config) : value;
        w[internalKey] = current;
      }
    });

    if (typeof current === 'function') {
      w[name] = current;
    }
  } catch (_) {
    if (typeof w[name] === 'function') {
      w[name] = wrapFunction(action, w[name], config);
    }
  }
}

function wrapAjaxPost(original: any, config: BeatLineViteConfig): any {
  if (typeof original !== 'function') return original;
  if (original.__beatlineC74Wrapped) return original;

  const wrapped = async function(this: any, action: string, payload: AnyRecord = {}) {
    if (ACTIONS[String(action)]) {
      try {
        return await callRest(String(action), payload || {}, config);
      } catch (error: any) {
        markFallback(String(action), String(error?.message || error));

        if ((config as any).debug || wAny().BEATLINE_VITE_DEBUG) {
          console.warn('[BeatLine full REST migration] REST failed, using original ajaxPost', {
            action,
            error: String(error?.message || error)
          });
        }

        const w = wAny();

        try {
          w.__beatlineC74BypassFetchHook = true;
          return await original.apply(this, [action, payload]);
        } finally {
          w.__beatlineC74BypassFetchHook = false;
        }
      }
    }

    return original.apply(this, [action, payload]);
  };

  wrapped.__beatlineC74Wrapped = true;
  wrapped.__beatlineC74Original = original;

  return wrapped;
}

function installAjaxPostHook(config: BeatLineViteConfig): void {
  const w = wAny();

  let current = typeof w.ajaxPost === 'function'
    ? wrapAjaxPost(w.ajaxPost, config)
    : w.ajaxPost;

  try {
    Object.defineProperty(w, 'ajaxPost', {
      configurable: true,
      enumerable: true,
      get() {
        return current;
      },
      set(value) {
        current = typeof value === 'function' ? wrapAjaxPost(value, config) : value;
      }
    });

    if (typeof current === 'function') {
      w.ajaxPost = current;
    }
  } catch (_) {
    if (typeof w.ajaxPost === 'function') {
      w.ajaxPost = wrapAjaxPost(w.ajaxPost, config);
    }
  }
}

export function installBeatLineFullRestBridge(config: BeatLineViteConfig): void {
  const w = wAny();

  if (w.__beatlineC74FullBridgeInstalled) return;
  w.__beatlineC74FullBridgeInstalled = true;

  getState();
  saveSummary();

  installFetchHook(config);
  installAjaxPostHook(config);

  Object.keys(GLOBAL_FUNCTIONS).forEach((name) => {
    installGlobalFunctionHook(name, GLOBAL_FUNCTIONS[name], config);
  });

  if ((config as any).debug || w.BEATLINE_VITE_DEBUG) {
    console.info('[BeatLine full REST migration] installed', {
      version: VERSION,
      actions: Object.keys(ACTIONS),
      globals: Object.keys(GLOBAL_FUNCTIONS)
    });
  }
}